import { Component, OnInit, OnDestroy, Input, Inject, ViewChild } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { combineLatest, firstValueFrom, Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';

import { WindowToken } from '../../services/window/window';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { UserService } from '../../services/user/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { RecordingService } from '../../services/recording/recording.service';
import { MuteService } from '../../services/mute/mute.service';
import { MicrophoneInfoMenuComponent } from '../../menus/microphone-info-menu/microphone-info-menu.component';
import { HeadphonesInfoMenuComponent } from '../../menus/headphones-info-menu/headphones-info-menu.component';
import { CameraInfoMenuComponent } from '../../menus/camera-info-menu/camera-info-menu.component';
import { VideoOffService } from '../../services/video-off/video-off.service';
import { IPadService } from '../../services/ipad/ipad.service';
import { RolesService } from '../../services/roles/roles.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { RoleLimits, Roles, VideoResolutions, VideoResolutionsForDisplay } from '@sc/types';
import { WalletService } from '../../services/wallet/wallet.service';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-equipment-list',
  templateUrl: './equipment-list.component.html',
  styleUrls: ['./equipment-list.component.scss'],
})
export class EquipmentListComponent implements OnInit, OnDestroy {
  @Input() ui: 'green-room' | 'name-menu' | 'test-equipment';
  @ViewChild('resolutionSelect') resolutionSelect: HTMLIonSelectElement;
  filteredNames = new Map<string, string>();

  participantEquipment$ = this.sessionsService.participantEquipment$;

  microphone$ = this.equipmentService.selectedMicrophone$;
  microphone: MediaDeviceInfo;
  microphones = new Map<string, MediaDeviceInfo>();
  mute = true;

  echoCancellation$ = this.equipmentService.echoCancellation$;
  recording: boolean;

  headphone$ = this.equipmentService.selectedHeadphones$;
  headphone: MediaDeviceInfo;
  headphones = new Map<string, MediaDeviceInfo>();

  camera$ = this.equipmentService.selectedCamera$;
  camera: MediaDeviceInfo;
  cameras = new Map<string, MediaDeviceInfo>();
  videoOff = true;

  selectedResolution$ = this.equipmentService.selectedResolution$;
  videoResolutions = this.equipmentService.videoResolutions;
  videoResolutionsForDisplay = VideoResolutionsForDisplay;
  resolutionNames = Object.keys(this.videoResolutionsForDisplay);

  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyCall$ = this.dailyService.dailyCall$;

  studioSession$ = this.sessionsService.studioSession$;
  plan$ = this.walletService.studioPlan$;
  role$ = this.rolesService.role$;
  roles = Roles;
  roleLimits = RoleLimits;
  backstage = false;

  isFirefox = false;
  isAppleDevice = false;
  isAndroid = false;
  isWindows = false;

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private deviceDetectorService: DeviceDetectorService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private equipmentService: EquipmentService,
    private iPadService: IPadService,
    private muteService: MuteService,
    private popoverController: PopoverController,
    private recordingService: RecordingService,
    private userService: UserService,
    private sessionsService: SessionsService,
    private toastrService: ToastrService,
    private rolesService: RolesService,
    private videoOffService: VideoOffService,
    private walletService: WalletService,
    @Inject(WindowToken) private window: Window
  ) {}

  ngOnInit() {
    this.setupDeviceCheck();
    this.setupDevices();
    this.setupMute();
    this.setupVideoOff();
    this.setupDeviceInfo();
    this.setupRecording();
    this.setupLocation();
  }

  setupLocation() {
    if (this.studioSession$.value.confType !== 'daily' || this.dolbyConversation$.value) {
      const dolbySub = this.dolbyService.location$.subscribe((location) => {
        this.backstage = location === 'backstage';
      });
      this.subs.push(dolbySub);
    }

    if (this.studioSession$.value.confType === 'daily' || this.dailyCall$.value) {
      // daily sessions have no backstage
      this.backstage = false;
    }
  }

  async setupDeviceInfo() {
    const deviceInfo = this.deviceDetectorService.getDeviceInfo();
    const isiPad = this.iPadService.check();

    // format OS Version
    if (deviceInfo.os === 'Mac') {
      if (isiPad) {
        deviceInfo.os = 'iPad';
        deviceInfo.device = 'iPad';
      } else {
        const osVersion = deviceInfo.os_version
          .replace(/([x])/g, '')
          .replace(/([s])/g, 'S')
          .replace(/-/g, ' ')
          .split(' ');
        deviceInfo.os_version = osVersion
          .map((word) => {
            if (word.length > 1) {
              if (word === 'Os') {
                return word[0].toUpperCase();
              } else {
                return word[0].toUpperCase() + word.substring(1);
              }
            }
          })
          .join(' ');
      }
    }
    await this.equipmentService.setDeviceInfo(deviceInfo);
  }

  setupMute() {
    const sub = this.muteService.mute$.subscribe((mute) => {
      this.mute = mute;
    });
    this.subs.push(sub);
  }

  setupVideoOff() {
    const sub = this.videoOffService.videoOff$.subscribe((videoOff) => {
      this.videoOff = videoOff;
    });

    this.subs.push(sub);
  }

  async getUserMedia() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    if (!this.equipmentService.hasPermission$.value) {
      this.equipmentService.hasPermission$.next([true, false]);
    }
    if (this.dolbyConversation$.value) this.dolbyService.enumerateDevices();
  }

  async setupDevices() {
    const sub = combineLatest([
      this.equipmentService.selectedEquipment$,
      this.equipmentService.systemDevices$,
    ]).subscribe(([equipment, devices]) => {
      if (!equipment) return;
      this.cameras.clear();
      this.headphones.clear();
      this.microphones.clear();
      devices.forEach((device) => {
        if (device.kind === 'audioinput') {
          this.microphones.set(device.deviceId, device);
          if (device.deviceId === this.microphone$.value.deviceId) {
            this.microphone = device;
          }
        }
        if (device.kind === 'audiooutput') {
          this.headphones.set(device.deviceId, device);
          if (device.deviceId === this.headphone$.value.deviceId) {
            this.headphone = device;
          }
        }
        if (device.kind === 'videoinput') {
          this.cameras.set(device.deviceId, device);
          if (device.deviceId === this.camera$.value.deviceId) {
            this.camera = device;
          }
        }
        if (!this.filteredNames.has(device.label)) {
          this.filteredNames.set(device.label, this.equipmentService.filterDeviceName(device.label));
        }
      });
    });
    this.subs.push(sub);

    if (this.dolbyConversation$.value) await this.dolbyService.location$.toPromise();
    if (this.backstage && !this.headphone) {
      await firstValueFrom(this.equipmentService.systemDevices$);
      this.headphone = this.equipmentService.getAvailableSystemDevice(null, 'audiooutput');
    }
  }

  selectMicrophone(event: ICustomEvent) {
    const eventTarget: IEventTarget = event.target;
    const microphone = eventTarget.value;

    if (!this.microphone$.value || microphone.deviceId === this.microphone$.value.deviceId) {
      return;
    }

    delete microphone.selected;

    this.equipmentService.selectedMicrophone$.next(microphone);

    this.analyticsService.track('selected microphone', {
      deviceID: microphone.deviceId,
      label: microphone.label,
    });
  }

  selectHeadphone(event: ICustomEvent) {
    const eventTarget: IEventTarget = event.target;
    const headphone = eventTarget.value;

    if (!headphone) return;

    if (!this.headphone$.value || headphone.deviceId === this.headphone$.value.deviceId) {
      return;
    }

    delete headphone.selected;

    this.equipmentService.selectedHeadphones$.next(headphone);
    this.analyticsService.track(`selected${this.backstage ? ' backstage' : ''} headphones`, {
      deviceID: headphone.deviceId,
      label: headphone.label,
    });
  }

  selectCamera(event: ICustomEvent) {
    const eventTarget: IEventTarget = event.target as HTMLInputElement;
    const camera = eventTarget.value;

    if (!this.camera$.value || camera.deviceId === this.camera$.value.deviceId) {
      return;
    }

    delete camera.selected;

    this.equipmentService.selectedCamera$.next(camera);

    this.analyticsService.track('selected camera', {
      deviceID: camera.deviceId,
      label: camera.label,
    });
  }

  selectResolution(event: ICustomEvent) {
    const value = event.detail.value as VideoResolutions;
    if (value === this.equipmentService.selectedResolution$.value) return;

    if (!this.plan$.value?.videoUHD && value === VideoResolutions.UHD) {
      const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(
        'You must upgrade to a paid Descript plan to enable 4K recording.',
        'Upgrade Plan',
        {
          progressBar: true,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          timeOut: 30 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );

      toast.toastRef.componentInstance.buttons = [
        {
          label: 'Upgrade',
          handler: () => {
            this.window.open('/account/subscription', '_blank');
          },
        },
      ];
      this.resolutionSelect.value = this.equipmentService.selectedResolution$.value;
      return;
    }

    this.equipmentService.setVideoQuality(value);
    this.analyticsService.track(`selected camera resolution from equipment list`, {
      label: this.camera.label,
      resolution: value,
    });
  }

  checkSupported(resolution: string) {
    const res = resolution as VideoResolutions;
    if (res === VideoResolutions.UHD && !this.plan$.value?.videoUHD && this.role$.value === Roles.GUEST) {
      return false;
    }
    if (
      res === VideoResolutions.UHD &&
      this.isWindows &&
      !this.sessionsService.studioSession$.value.windows4KOverride
    ) {
      return false;
    }
    return this.participantEquipment$.value?.supportedResolutions?.includes(res);
  }

  async openMicrophoneInfoMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: MicrophoneInfoMenuComponent,
      cssClass: 'info-menu',
      event: click,
    });

    await menu.present();
    await this.analyticsService.track('opened microphone info');
  }

  async openHeadphonesInfoMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: HeadphonesInfoMenuComponent,
      cssClass: 'info-menu',
      event: click,
    });

    await menu.present();

    await this.analyticsService.track('opened headphones info');
  }

  async openCameraInfoMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: CameraInfoMenuComponent,
      cssClass: 'info-menu',
      event: click,
    });

    await menu.present();
    await this.analyticsService.track('opened camera info');
  }

  compareDevices(deviceA: MediaDeviceInfo, deviceB: MediaDeviceInfo) {
    return deviceA && deviceB ? deviceA.deviceId === deviceB.deviceId : deviceA === deviceB;
  }

  async toggleVideoOff() {
    this.videoOffService.setVideoOff(
      this.sessionsService.studioSessionID$.value,
      this.userService.activeUser$.value.uid,
      !this.videoOff
    );
    await this.analyticsService.track('toggled video off', { enabled: !this.videoOff });
  }

  async toggleMute() {
    this.muteService.setMute(
      this.sessionsService.studioSessionID$.value,
      this.userService.activeUser$.value.uid,
      !this.mute
    );
    await this.analyticsService.track('toggled mute', { enabled: !this.mute });
  }

  setupRecording() {
    const sub = this.recordingService.recording$.subscribe((recording: boolean) => {
      if (this.ui === 'green-room' || this.ui === 'test-equipment') return; // prevent dropdowns from being disabled while in green room
      this.recording = recording;
    });

    this.subs.push(sub);
  }

  setupDeviceCheck() {
    const deviceInfo = this.deviceDetectorService.getDeviceInfo();

    const iPad = this.iPadService.check();
    const iPhone = deviceInfo.device === 'iPhone';
    const safari = deviceInfo.browser === 'Safari';

    this.isAppleDevice = iPhone || iPad || safari;
    this.isFirefox = deviceInfo.browser === 'Firefox' || deviceInfo.browser === 'firefox';
    this.isAndroid = deviceInfo.os === 'Android';
    this.isWindows = deviceInfo.os === 'Windows';
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
